import * as React from 'react';
import { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate } from 'react-router-dom';
import { columnData } from './mockData';
import { getChipColor } from '../../utils/user';
import Actions from './Actions/Actions.component';
import seedUserApplicationsIfNeeded from './seedUserApplications';
import './UserApplications.styles.scss';


seedUserApplicationsIfNeeded();

const STATUS_LABEL = {
  'applied':      'Applied',
  'in-progress':  'In Progress',
  'in progress':  'In Progress',
  'inprogress':   'In Progress',
  'completed':    'Completed',
  'rejected':     'Rejected',
  'cancelled':    'Cancelled',
};

const normalizeStatus = (raw) =>
  STATUS_LABEL[(raw || '').trim().toLowerCase()] || raw || '—';

// Handles both dd/mm/yyyy and ISO timestamp strings.
const fmtStoredDate = (val) => {
  if (!val) return '—';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) return val;
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  } catch { return val; }
};

// Fields in the exact order they map to columnData headers.
const FIELDS = ['orderId', 'serviceName', 'serviceStatus', 'serviceLastDate', 'applicationFee', 'cscCharge', 'amount', 'lastUpdated', 'actions'];
const FEE_FIELDS = new Set(['applicationFee', 'cscCharge', 'amount']);

const getUserApplications = () => {
  try {
    const apps = JSON.parse(localStorage.getItem('userApplications') || '[]');
    return apps.map((app) => ({
      orderId:         app.orderId,
      serviceName:     app.serviceName,
      serviceStatus:   normalizeStatus(app.serviceStatus),
      serviceLastDate: app.serviceLastDate,
      applicationFee:  app.applicationFee != null ? Number(app.applicationFee) : null,
      cscCharge:       app.cscCharge != null ? Number(app.cscCharge) : null,
      amount:          app.amount,
      lastUpdated:     fmtStoredDate(app.lastUpdated || app.applicationLastUpdatedDate),
      actions:         '',
    }));
  } catch { return []; }
};

const tableContainerSx = { boxShadow: 'none', borderRadius: 0 };
const tableSx = { minWidth: 960 };

const UserApplications = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [rowsData, setRowsData] = useState(getUserApplications);

  // Re-read on every mount so freshly applied applications always appear.
  useEffect(() => {
    setRowsData(getUserApplications());
  }, []);

  const filteredRows = searchQuery.trim()
    ? rowsData.filter((r) =>
        r.serviceName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(r.orderId).includes(searchQuery)
      )
    : rowsData;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(columnId);
  };

  const sortedRowsData = stableSort(filteredRows, getComparator(order, orderBy));

  return (
    <div className="user-applications-container">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <ArrowBackIosNewIcon className="back-btn-icon" />
          <span>Back</span>
        </button>
        <div className="page-header__center">
          <h1 className="page-title">My Applications</h1>
          <p className="page-subtitle">Track and manage your submitted applications</p>
        </div>
        <div className="page-header__spacer" aria-hidden="true" />
      </div>

      <div className="applications-table-container">
        <div className="table-header">
          <h2 className="header-title">Detailed Overview</h2>
          <p className="header-subtitle">View all your applications and their current status</p>
        </div>

        <div className="table-search-bar">
          <TextField
            size="small"
            placeholder="Search by service name or order ID…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => { setSearchQuery(''); setPage(0); }}
                    edge="end"
                    sx={{ color: 'text.secondary', '&:hover': { color: '#0B4A50' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 320 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                fontSize: '0.875rem',
                '& fieldset': { borderColor: 'rgba(12,74,80,0.22)' },
                '&:hover fieldset': { borderColor: '#0B4A50' },
                '&.Mui-focused fieldset': { borderColor: '#0B4A50' },
              },
            }}
          />
          {searchQuery && (
            <span className="table-search-bar__count">
              {filteredRows.length} result{filteredRows.length === 1 ? '' : 's'}
            </span>
          )}
        </div>

        <div className="table-scroll-hint" aria-hidden="true">Scroll to see more →</div>

        <TableContainer component={Paper} className="table-wrapper" sx={tableContainerSx}>
          <Table sx={tableSx} stickyHeader aria-label="applications table">
            <TableHead>
              <TableRow>
                {columnData.map((item, index) => (
                  <TableCell
                    key={index}
                    align="center"
                    onClick={() => handleSort(item)}
                  >
                     {item}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRowsData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {FIELDS.map((field, colIndex) => {
                    const value = row[field];
                    if (field === 'actions') {
                      return (
                        <TableCell key={colIndex} align="center">
                          <Actions row={row} />
                        </TableCell>
                      );
                    }
                    if (field === 'serviceStatus') {
                      return (
                        <TableCell key={colIndex} align="center">
                          <Chip label={value} size="small" variant="filled" color={getChipColor(value)} />
                        </TableCell>
                      );
                    }
                    if (FEE_FIELDS.has(field)) {
                      return (
                        <TableCell key={colIndex} align="center">
                          {value != null
                            ? <span className="amount-value">{`₹${value}/-`}</span>
                            : <span className="amount-na">—</span>}
                        </TableCell>
                      );
                    }
                    return <TableCell key={colIndex} align="center">{value || '—'}</TableCell>;
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </div>

      {rowsData.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3 className="empty-title">No Applications Found</h3>
          <p className="empty-description">You haven't submitted any applications yet. Start by browsing available services.</p>
        </div>
      )}
    </div>
  );
};

// Function to stabilize sort order
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Function to compare values for sorting
function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a[orderBy], b[orderBy])
    : (a, b) => -descendingComparator(a[orderBy], b[orderBy]);
}

// Helper function for comparing values for sorting
function descendingComparator(a, b) {
  if (b < a) {
    return -1;
  }
  if (b > a) {
    return 1;
  }
  return 0;
}

export default UserApplications;
